import classNames from 'classnames';
import {
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import superagent from 'superagent';

import {
  Eye,
  EyeCrossed,
} from '../../assets/js/svg/gui';
import {
  Button,
  PaxityProgress,
  Column,
  ConfirmPopup,
  FullPopup,
  Row,
  SvgColorImg,
  TextInput,
} from '../../components';
import SupportLink from '../../components/SupportLink';
import {
  useAuth,
  useBrowser,
  useDisplay,
  useTownAssets,
} from '../../stores';
import {
  config,
  logError,
  mobileAppStores,
} from '../../utils';

import LoginSteps from './components/LoginSteps';

function LoginPage() {
  const { auth } = useAuth();
  const { assets } = useTownAssets();
  const { translate } = assets;
  const { display } = useDisplay();
  const { browser } = useBrowser();
  const [searchParams] = useSearchParams();
  const redirectUsername = searchParams.get('u');
  const [username, setUsername] = useState(redirectUsername || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [warnMessage, setWarnMessage] = useState();
  const [confirmRecovery, setConfirmRecovery] = useState(false);
  const [unfitDismissed, setUnfitDismissed] = useState(false);

  // A handheld that only needs turning is nudged; one that cannot fit either way is sent to the app
  const tooSmall = browser.touchPrimary && (!display.shellFitsWindow);
  const rotatable = tooSmall && display.portrait && display.shellFitsRotated;
  const unfit = tooSmall && (!rotatable);
  const stores = mobileAppStores(assets?.language?.language);

  // www-admin-anon lands here with a one-time code in the fragment, which
  // never reaches a server or a log; it is dropped from the URL before use
  useEffect(
    () => {
      const anonLoginReqId = new URLSearchParams(window.location.hash.slice(1)).get('px');
      if (!anonLoginReqId) return;

      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      auth.server.loginAdmin(anonLoginReqId).then((success) => {
        if (!success) setWarnMessage('loginFailed');
      });
    },
    [auth.server],
  );

  const connect = async () => {
    setWarnMessage();
    const success = await auth.server.login(username, password);

    if (!success) {
      console.log('Connect failed');
      setWarnMessage('loginFailed');
    // } else {
    //   console.log('Connect ok');
    }
  };

  const handleConnectClick = () => {
    // Use void to make it clear we do not want to wait this async function:
    void connect();
  };

  const handleBackClick = () => {
    window.location.href = `${config.anon.www.corporate.login}?u=${redirectUsername}`;
  };

  const usernameOnChange = (event) => {
    setUsername(event.target.value
      .replace(' ', '')
      .trim());
    setWarnMessage();
  };

  const passwordOnChange = (event) => {
    setPassword(event.target.value
      .replace(' ', '')
      .trim());
    setWarnMessage();
  };

  const handlePasswordKeyDown = async (event) => {
    if (event.keyCode === 13) {
      if (!(auth.server.reqPending || (username === '') || (password === ''))) {
        // Use void to make it clear we do not want to wait this async function:
        void connect();
      }
    }
  };

  const handleShowPasswordClick = () => {
    setShowPassword((currentShow) => (!currentShow));
  };

  // A recovery request carries the typed username as is: the server pattern gates it here
  const recoverable = (username !== '') && (auth.server.regex?.username?.test(username) ?? false);

  const onClickRecovery = () => {
    setConfirmRecovery(true);
  };

  const recover = async () => {
    setConfirmRecovery(false);
    setWarnMessage();

    const url = config.town.back.urlPrefix
              + config.town.back.auth.recoverReq;

    const payload = {
      username,
    };

    try {
      await superagent
        .post(url)
        .set('Content-Type', 'application/json') // headers
        .send(payload);
    } catch (err) {
      logError(
        `recover err=${err}`,
        'LoginPage.recover.error',
        {
          err: err?.message,
          ...(err?.status && { status: err.status }),
          url,
        },
      );
      setWarnMessage('recoveryRequestError');
      return null;
    }

    return setWarnMessage('recoveryRequestSent');
  };

  return (
    <main
      className="h-screen w-screen max-h-screen max-w-screen p-2 absolute l-0 overflow-hidden flex flex-col items-center justify-center font-light text-start text-base bg-white"
      // bg-pxInitBgnd
      // className="h-screen w-screen max-h-screen max-w-screen p-2 absolute l-0 overflow-hidden
      // flex flex-row items-center justify-center font-light
    >
      <Column className="flex-1 w-full" between>
        {(config.dev && (!config.live))
          ? (
              <span className="self-start">
                {'Note: accéder à ce site via '}
                <a href={config.town.www.url}>{config.town.www.url}</a>
              </span>
            )
          : <span />}

        <Column>
          <img
            src="/images/png/logo.png"
            alt="paxity"
            className="w-24 mb-10"
          />

          {/* Loading state - waiting for initial auth status */}
          {(auth.server.reqPending && (auth.info === undefined)) && (
            <div className="flex flex-col items-center gap-4">
              <PaxityProgress />
              <span className="text-pxGrey">{`${translate('connectingToServer', 'statements')}...`}</span>
            </div>
          )}

          {/* Error state - server unreachable */}
          {(auth.server.serverError === 'unreachable') && (
            <div className="mb-6 p-4 bg-pxUiAlertBg border border-pxUiAlert text-pxUiAlert rounded text-center">
              <p className="font-medium">{translate('serverUnreachable', 'statements')}</p>
              <p className="text-sm mt-1">{translate('serverUnreachableHint', 'statements')}</p>
              <p className="text-sm mt-2">
                <SupportLink />
              </p>
            </div>
          )}

          {(auth.server.loginPending) && (auth.server.serverError !== 'unreachable')
            ? <LoginSteps />
            : null}

          {/* Login form - ready for authentication */}
          {((!auth.server.reqPending) || (auth.info !== undefined))
            && (auth.server.serverError !== 'unreachable')
            && (!auth.server.loginPending)
            && (
              <form
                action="/login"
                method="post"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleConnectClick();
                }}
                className="grid grid-cols-2-auto gap-y-5 gap-x-2 justify-items-center items-center"
              >
                <TextInput
                  id="username"
                  name="username"
                  placeholder={translate('username')}
                  fillType="username"
                  autoFill
                  type="text"
                  onChange={usernameOnChange}
                  value={username}
                  disabled={!!redirectUsername}
                  focus={!redirectUsername}
                />

                { (redirectUsername)
                  ? (
                      <Button
                        onClick={handleBackClick}
                        imgSrc="/images/svg/gui/cross.svg"
                        neutralColor
                      />
                    )
                  : <div />}

                <TextInput
                  id="password"
                  name="password"
                  placeholder={translate('password')}
                  showPassword={showPassword}
                  fillType="storedPassword"
                  autoFill
                  className="flex-1"
                  onChange={passwordOnChange}
                  value={password}
                  disabled={!!auth.server.reqPending}
                  onKeyDown={handlePasswordKeyDown}
                  focus={!!redirectUsername}
                />

                <Button
                  aria-label="toggle password visibility"
                  onClick={handleShowPasswordClick}
                  size="large"
                  Image={(showPassword) ? Eye : EyeCrossed}
                  noFrame
                  imgColor="#808080"
                />

                <Row gap={6} className="w-full col-span-2">
                  <Button
                    onClick={handleConnectClick}
                    disabled={(!!auth.server.reqPending) || (auth.server.serverError === 'unreachable') || (username === '') || (password === '')}
                    imgSrc="/images/svg/gui/play.svg"
                    submitType
                    baseColor
                  />
                </Row>

                {(rotatable)
                  ? (
                      <Column gap={2} className="w-full col-span-2 text-pxGrey">
                        <SvgColorImg
                          src="/images/svg/gui/rotateMobile.svg"
                          imgColorAsCssFill
                          description={translate('loginRotateForBetterDisplay', 'statements')}
                          className="h-16 w-16 shrink-0 fill-pxGrey"
                        />

                        <span className="font-bold italic">
                          {translate('loginRotateForBetterDisplay', 'statements')}
                        </span>
                      </Column>
                    )
                  : null}

                <Row className="w-full col-span-2">
                  {(warnMessage)
                    ? (
                        <Row gap={2}>
                          <span className="text-pxUiAlert">{translate(warnMessage, 'statements')}</span>
                          <SupportLink />
                        </Row>
                      )
                    : (
                        <span>
                          {(auth.server.loginPendingExpired)
                            ? translate('loginPendingExpired', 'statements')
                            : ' '}
                        </span>
                      )}
                </Row>

                <button
                  type="button"
                  className={classNames(
                    'text-pxTxtLink col-span-2',
                    {
                      'opacity-60': !recoverable,
                    },
                  )}
                  onClick={onClickRecovery}
                  disabled={!recoverable}
                >
                  {translate('passwordLost', 'statements')}
                </button>
              </form>
            )}
        </Column>

        {(config.dev && (!config.live))
          ? (
              <Row end className="w-full">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="/home"
                >
                  Demo
                </a>
                -
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={config.anon.www.admin}
                >
                  Admin
                </a>
              </Row>
            )
          : <span />}
      </Column>

      {(unfit && (!unfitDismissed))
        ? (
            <FullPopup
              title={translate('desktopInterface', 'uiTitle')}
              onClose={() => { setUnfitDismissed(true); }}
              small
              className="min-w-[300px]"
              contentClassName="h-full w-full relative overflow-hidden flex flex-col gap-4"
            >
              <Column crossStart gap={2} className="flex-1">
                <span>{translate('loginInterfaceForDesktop', 'statements')}</span>
                <span>{translate('loginUseMobileAppInstead', 'statements')}</span>
              </Column>

              <Row gap={4} evenly className="w-full flex-wrap">
                {stores.map((store) => (
                  <a
                    key={store.key}
                    href={store.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={store.label}
                  >
                    <img src={encodeURI(store.badge)} alt="" className="h-11" />
                  </a>
                ))}
              </Row>
            </FullPopup>
          )
        : null}

      {(confirmRecovery)
        ? (
            <ConfirmPopup
              question={[
                `${translate('sendRecoveryCodeTo', 'statements', { username })}?`,
                translate('recoveryCodeOnlyIfActive', 'statements'),
              ]}
              // Use void to make it clear we do not want to wait this async function:
              onConfirm={() => { void recover(); }}
              onReject={() => { setConfirmRecovery(false); }}
            />
          )
        : null}
    </main>
  );
}

export default LoginPage;
